import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useListCustomers } from "@/lib/api/customer";
import { deliveryTermsOptions, useListMarketZones } from "@/lib/api/generalContract";
import { TtOrderMetadata, TtOrderSnapshot } from "@/lib/api/risk/brokerage/tt";
import { NeedsAttentionRow } from "@/lib/api/risk/brokerage/tt/v2";
import { SelectOptionItem } from "@/lib/models/ui";
import { genIdOptionList } from "@/lib/utils";
import { rankLinkedOrderCandidates } from "@/lib/tt/linkedOrders";
import { GeneralContractField } from "./GeneralContractField";
import { CustomerSplitsField } from "./CustomerSplitsField";
import { CropYearField } from "./CropYearField";
import { LinkedOrderField } from "./LinkedOrderField";
import { OrderTypeField } from "./OrderTypeField";
import { useAttachOrderForm } from "./useAttachOrderForm";

interface AttachOrderFormProps {
  guidOrderId: string;
  snapshot: TtOrderSnapshot;
  existingMetadata: TtOrderMetadata | null;
  /**
   * Already-fetched orders offered as link targets. The order being edited is
   * excluded and the rest are ranked by text-field similarity.
   */
  candidateOrders?: NeedsAttentionRow[];
  /** Called after a successful save. Parent decides what to do (e.g. keep panel open). */
  onSaved?: () => void;
}

export function AttachOrderForm({
  guidOrderId,
  snapshot,
  existingMetadata,
  candidateOrders = [],
  onSaved,
}: AttachOrderFormProps) {
  // NOTE: do not change the customer hook params or share with other callsites
  // — different params return different shapes / permissions.
  const customersResult = useListCustomers({ minimal: true });

  const allCustomers = customersResult.data?.data ?? [];

  const marketZonesResult = useListMarketZones();
  const marketZones = marketZonesResult.data?.data ?? [];
  const marketZoneOptions: SelectOptionItem<string>[] = genIdOptionList(marketZones, "market_zone");

  const form = useAttachOrderForm({
    guidOrderId,
    snapshot,
    existingMetadata,
    customers: allCustomers.map((c: { id: string; long_name?: string; short_name?: string; hasRiskProfile?: boolean }) => ({
      id: c.id,
      longName: c.long_name || c.short_name || "",
      hasRiskProfile: c.hasRiskProfile,
    })),
    linkedOrderCandidates: candidateOrders,
    onSaved,
  });

  const rankedLinkedOrders = rankLinkedOrderCandidates(
    guidOrderId,
    snapshot,
    candidateOrders,
  );

  const customerOptions: SelectOptionItem<string>[] = useMemo(() => {
    return allCustomers
      .filter((c: { hasRiskProfile?: boolean }) => {
        if (form.values.isNonClient) return !c.hasRiskProfile;
        return !!c.hasRiskProfile;
      })
      .map((c: { id: string; long_name?: string; short_name?: string }) => ({
        value: c.id,
        label: c.long_name || c.short_name || "",
      }));
  }, [allCustomers, form.values.isNonClient]);

  const suggestionCustomerLabel = useMemo(() => {
    if (!form.suggestions.customerId) return undefined;
    const opt = allCustomers.find((c: { id: string }) => c.id === form.suggestions.customerId);
    return (opt as { long_name?: string; short_name?: string } | undefined)?.long_name
      ?? (opt as { short_name?: string } | undefined)?.short_name;
  }, [form.suggestions.customerId, allCustomers]);

  const showCustomerSuggestion =
    !!form.suggestions.customerId &&
    !form.values.customerSplits.some((s) => s.customer_id === form.suggestions.customerId);

  const showOrderTypeSuggestion =
    !!form.suggestions.orderType && form.values.orderType !== form.suggestions.orderType;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void form.submit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border rounded-md p-3">
      <h3 className="text-sm font-semibold">
        {existingMetadata ? (!form.isUpdatable ? "Info" : "Update Metadata") : "Set Metadata"}
      </h3>

      <OrderTypeField
        isEditable={form.isUpdatable}
        orderType={form.values.orderType}
        otcContractMode={form.values.otcContractMode}
        otcContractType={form.values.otcContractType}
        amendmentType={form.values.amendmentType}
        contractNumber={form.values.contractNumber}
        errorOrderType={form.errors.orderType}
        errorOtcContractType={form.errors.otcContractType}
        errorAmendmentType={form.errors.amendmentType}
        suggestionOrderType={form.suggestions.orderType}
        showOrderTypeSuggestion={showOrderTypeSuggestion}
        onChangeOrderType={form.changeOrderType}
        onChangeOtcContractMode={form.changeOtcContractMode}
        onChangeOtcContractType={(v) => form.setField("otcContractType", v)}
        onChangeAmendmentType={(v) => form.setField("amendmentType", v)}
        onChangeContractNumber={(v) => form.setField("contractNumber", v)}
        onApplyOrderTypeSuggestion={form.applyOrderTypeSuggestion}
      />

      <CropYearField
        isEditable={form.isUpdatable}
        value={form.values.cropYear}
        error={form.errors.cropYear}
        onChange={(v) => form.setField("cropYear", v)}
        linkedOrderEnabled={form.values.linkedOrderEnabled}
        onChangeLinkedOrderEnabled={form.changeLinkedOrderEnabled}
      />

      {form.values.linkedOrderEnabled && (
        <LinkedOrderField
          isEditable={form.isUpdatable}
          links={form.values.linkedOrders}
          candidateOrders={rankedLinkedOrders}
          error={form.errors.linkedOrders}
          onChangeLink={form.changeLinkedOrder}
          onAdd={form.addLinkedOrder}
          onRemove={form.removeLinkedOrder}
        />
      )}

      <CustomerSplitsField
        isEditable={form.isUpdatable}
        splits={form.values.customerSplits}
        totalQuantity={form.totals.totalQuantity}
        lhcRemainder={form.totals.lhcRemainder}
        customerOptions={customerOptions}
        customersLoading={customersResult.isLoading}
        isNonClient={form.values.isNonClient}
        shouldIgnore={form.values.shouldIgnore}
        error={form.errors.customerSplits}
        suggestionCustomerId={form.suggestions.customerId}
        suggestionCustomerLabel={suggestionCustomerLabel}
        showCustomerSuggestion={showCustomerSuggestion}
        onChangeQuantity={form.changeCustomerSplitQuantity}
        onChangeCustomer={form.changeCustomerSplitCustomer}
        onAdd={form.addCustomerSplit}
        onRemove={form.removeCustomerSplit}
        onApplyCustomerSuggestion={form.applyCustomerSuggestion}
        onChangeIsNonClient={(v) => form.setField("isNonClient", v)}
        onChangeShouldIgnore={(v) => form.setField("shouldIgnore", v)}
      />

      {form.values.orderType === "cash" && (
        <GeneralContractField
          isEditable={form.isUpdatable}
          basis={form.values.basis}
          marketZoneId={form.values.marketZoneId}
          deliveryTerms={form.values.deliveryTerms}
          marketZoneOptions={marketZoneOptions}
          marketZonesLoading={marketZonesResult.isLoading}
          deliveryTermsOptions={deliveryTermsOptions}
          errorBasis={form.errors.basis}
          errorMarketZone={form.errors.marketZoneId}
          errorDeliveryTerms={form.errors.deliveryTerms}
          onChangeBasis={(v) => form.setField("basis", v)}
          onChangeMarketZone={(v) => form.setField("marketZoneId", v)}
          onChangeDeliveryTerms={(v) => form.setField("deliveryTerms", v)}
        />
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={form.isSubmitting || !form.isValid || !form.isUpdatable}>
          {form.isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}
